import { JSX, createContext, useCallback, useEffect, useMemo, useState } from 'react';
import * as cognitoService from '@services/authentication';
import { UserData } from '@services/authentication';
import * as Sentry from '@sentry/react';
import { CognitoUser } from 'amazon-cognito-identity-js';

// see: https://www.sammeechward.com/cognito-user-pool-react

// export type AuthContextType = {
//   authState: boolean;
//   setAuthState: (state: boolean) => void;
// };

// TODO: what do we need to the user object in the context?
// 1. what do we need to the user object in the context?
interface SignInResponse {
  newPasswordRequired: boolean;
  userAttributes?: Record<string, string | undefined>;
  requiredAttributes?: string[];
  username?: string;
}
interface AuthContextProps {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserData | null;
  cognitoUser: CognitoUser | null;
  signIn: (username: string, password: string, localStorage: boolean) => Promise<void | SignInResponse>;
  signOut: () => void;
  isUserInGroup: (groupName: string) => boolean;
}

const AuthContext = createContext<AuthContextProps>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  cognitoUser: null,
  signIn: async () => {},
  signOut: () => {},
  isUserInGroup: () => false,
});

interface AuthProviderProps {
  children: React.ReactNode;
}
function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<UserData | null>(null);
  const [cognitoUser, setCognitoUser] = useState<CognitoUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [groups, setGroups] = useState<string[]>([]);

  const getCurrentUser = async () => {
    try {
      const currentUser = await cognitoService.getUser();
      const userGroups = await cognitoService.getUserGroups();
      setUser(currentUser);
      setGroups((userGroups || []).map((g) => g.toLowerCase().trim())); // forces to lower case
    } catch {
      setUser(null);
      setGroups([]);
    }
  };

  useEffect(() => {
    getCurrentUser()
      .then(() => setIsLoading(false))
      .catch(() => setIsLoading(false));
  }, []);

  const signIn = useCallback(async (username: string, password: string, rememberMe: boolean) => {
    const result = await cognitoService.signIn(username, password, rememberMe);

    // the user needs to reset their password, forced password reset by cognito
    if ('userAttributes' in result && 'requiredAttributes' in result) {
      // Return the newPasswordRequired response to the caller
      // this is hacky to pass around the cognitoUser object that tried to login inn the Context
      setCognitoUser(result.currentUser);
      return {
        newPasswordRequired: true,
        userAttributes: result.userAttributes,
        requiredAttributes: result.requiredAttributes,
      };
    }

    await getCurrentUser();

    return;
  }, []);

  const signOut = useCallback(() => {
    cognitoService.signOut();
    setUser(null);
    setGroups([]);
    Sentry.setUser(null);
  }, []);

  const isUserInGroup = useCallback(
    (groupName: string): boolean => {
      return groups.includes(groupName.trim().toLowerCase());
    },
    [groups]
  );

  const contextValue: AuthContextProps = useMemo(
    () => ({
      isAuthenticated: !!user,
      isLoading,
      user,
      cognitoUser,
      signIn,
      signOut,
      isUserInGroup,
    }),
    [isLoading, user, cognitoUser, signIn, signOut, isUserInGroup]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export { AuthProvider, AuthContext };
